export const HELP_GROUPS = [
  {
    title: '从灵感到剧本',
    items: [
      {
        id: 'idea-to-script',
        title: '把一个点子养成剧本',
        summary: '从一句话开始，依次完成设定、故事大纲、分集大纲和正文。',
        eyebrow: '第一次用小影，从这里开始',
        intro: '别急着一口气许愿“帮我写 80 集”。先让小影认识你的世界和人物，否则男主写到第 12 集，可能会突然忘记自己怕水。',
        sections: [
          {
            id: 'start-with-one-line',
            title: '先给一句能继续追问的话',
            paragraphs: ['在首页中央输入框写清楚主角、目标和麻烦。信息不用多，但要让冲突露个头。'],
            prompt: '一个替姐姐嫁进豪门的女孩，发现姐姐的死与这家人有关，而新婚丈夫似乎也在调查同一件事。',
            tip: '好点子不是百科全书。主角想要什么、什么在拦她，先说清这两件事就够了。',
            visual: 'idea',
          },
          {
            id: 'build-the-foundation',
            title: '让 Agent 先搭地基',
            paragraphs: ['小影会沿着创意设定、故事大纲、分集大纲、正文逐步推进。前面的决定会进入后续写作上下文。'],
            steps: ['确认世界观、人物关系和不可打破的规则', '确定整部故事怎么起、怎么翻、怎么收', '拆出每一集的冲突、信息流和结尾钩子', '逐集写正文，并检查与前文是否打架'],
            warning: '人物设定还没确认时就催正文，速度看似快，返工通常更快。',
            visual: 'pipeline',
          },
          {
            id: 'write-and-check',
            title: '边写边确认，不把伏笔写成失忆',
            paragraphs: ['每集生成后，小影会检查剧情事实、人物认知和伏笔。初稿需要你阅读并采用，才会成为定稿；有严重冲突时，需要先修订。'],
            bullets: ['初稿有问题：告诉 Agent 哪一处需要修改', '查看故事记忆：核对来源集数与原文，必要时纠正或停用', '只想改一句：选中文字做局部调整，不必重写整集'],
            tip: '把“他为什么知道这件事”也写进计划里，悬疑剧会少很多超能力式破案。',
            visual: 'review',
          },
          {
            id: 'move-to-canvas',
            title: '剧本稳定后，再去画布开机',
            paragraphs: ['确认剧本后进入无限画布，拆人物、场景、道具和镜头，再生成参考图、首帧、视频与音频。'],
            steps: ['在写作台点击“去画布制作”', '选择已定稿的集数并确认创建画布', '在画布中确认人物、场景和分镜', '先确认参考图，再生成首帧和视频'],
            visual: 'canvas',
          },
        ],
      },
      {
        id: 'existing-script',
        title: '粘贴、上传已有剧本',
        summary: '已有正文不必从头聊；先审核，再决定是否保存和继续改。',
        eyebrow: '手里已经有稿子',
        intro: '有现成剧本就别假装第一次见面。把它交给审核工具，小影会先读稿，再说哪里能拍、哪里会让制片人血压升高。',
        sections: [
          {
            id: 'paste-text',
            title: '直接粘贴文本',
            paragraphs: ['进入“写剧本 → 审核剧本”，选择“粘贴文本”，填写标题后粘贴完整内容。分集大纲、剧情简介或完整剧本都可以提交。'],
            steps: ['选择“粘贴文本”', '填写一个以后找得到的标题', '粘贴内容并开始审核', '查看结论、问题和修改建议'],
            tip: '超长剧本建议保留清楚的“第 1 集 / 第 2 集”标记，机器和人类都会更轻松。',
            visual: 'upload',
          },
          {
            id: 'upload-file',
            title: '上传文件',
            paragraphs: ['在首页点击“上传剧本审核”，或进入“写剧本 → 审核剧本 → 上传文件”。选择 PDF、DOCX、DOC 或 TXT 文件后点击“开始审核”，即可查看审核报告。'],
            bullets: ['扫描版 PDF 可能提取不到完整文字', '复杂表格、批注和页眉可能影响结构识别', '上传后先抽查人物名和集号是否完整'],
            visual: 'document',
          },
          {
            id: 'import-boundary',
            title: '上传后会发生什么',
            paragraphs: ['上传用于提取正文并审核，不会自动拆分集数或覆盖已有写作项目。审核完成后，可选择“保存为新剧本”，正文会作为新项目的第 1 集导入稿保留。'],
          },
        ],
      },
      {
        id: 'writing-workbench',
        title: '认识写作台',
        summary: '章节树、正文编辑、历史与恢复、故事记忆与 Agent，各司其职。',
        eyebrow: '不要被按钮包围',
        intro: '写作台不是驾驶舱，不需要先考执照。左边找内容，中间写正文，右边找 Agent；迷路时记住这三句话。',
        sections: [
          { id: 'workbench-layout', title: '三个主要区域', paragraphs: ['左侧是故事结构和集数，中间是正文，右侧是可展开、收起和最大化的小影 Agent。'], bullets: ['左：创意设定、故事大纲、人物和正文集数', '中：剧本正文与局部 AI 调整', '右：Agent 对话、建议和运行状态'], visual: 'editor' },
          { id: 'autosave', title: '保存与版本', paragraphs: ['正文编辑会自动保存，请留意保存状态。通过“历史与恢复”查看故事资料、分集大纲和正文的修改记录；恢复前先核对内容及受影响的集数。'], tip: '看到“保存中”先别关页面；保存失败时，按提示重试。', visual: 'save' },
          { id: 'workbench-status', title: '和 Agent 继续写作', paragraphs: ['在右侧告诉 Agent 要完善的设定、集数或具体段落。生成初稿后先阅读，再选择修改或采用；已定稿内容可以继续交给画布制作。'], visual: 'agent' },
        ],
      },
    ],
  },
  {
    title: '和小影 Agent 共创',
    items: [
      {
        id: 'agent-collaboration',
        title: '把 Agent 当编剧搭档',
        summary: '说清目标、范围和禁区，比一句“高级一点”有用得多。',
        eyebrow: 'Agent 不是许愿池',
        intro: '“帮我改好一点”就像对演员说“你演得再有感觉一点”。不是完全没用，但大家都只能礼貌地猜。',
        sections: [
          { id: 'good-instruction', title: '一条好指令的四个零件', paragraphs: ['告诉小影改哪里、想达到什么效果、必须保留什么、不要做什么。'], prompt: '只改第 12 集这场审讯。把冲突提前，但保留林乔不知道姐姐遗书的事实；不要新增角色，也不要改结尾钩子。', bullets: ['范围：哪一集、哪一场或哪一段', '目标：加快、变狠、补逻辑还是润色', '保留：不能动的人物事实和结尾', '禁区：不要加戏、不要剧透、不要整集重写'], visual: 'agent' },
          { id: 'use-context', title: '给它上下文，但别倒进整个仓库', paragraphs: ['引用当前集、人物设定或明确的参考内容。资料越多不等于越聪明，重点是说明每份资料拿来做什么。'], tip: '两份准确参考，通常比二十份“你自己看看”更靠谱。', visual: 'context' },
          { id: 'agent-states', title: '收起、展开和最大化', paragraphs: ['需要专心写正文时收起 Agent；边写边问时展开；阅读长回复时最大化。切换显示方式会保留当前对话。'], visual: 'agent' },
        ],
      },
      {
        id: 'candidate-and-facts',
        title: '初稿与故事记忆',
        summary: '阅读并采用初稿，查看支撑后续剧情的故事记忆。',
        eyebrow: '连续性保险丝',
        intro: '初稿先由你决定是否采用。采用后，小影会记录有原文依据的剧情，帮助后续集数保持连贯。',
        sections: [
          { id: 'candidate-gate', title: '初稿为什么还不是定稿', paragraphs: ['生成的初稿不会直接替换定稿。有严重剧情冲突时，先按问题提示修订；检查通过后，再选择采用。'], bullets: ['阅读本集内容和检查提示', '需要调整时选择“改改这集”', '确认满意后采用，再继续下一集'], visual: 'review' },
          { id: 'fact-confirmation', title: '查看与纠正故事记忆', paragraphs: ['在“故事记忆”中查看人物、事件与伏笔的记录，并核对来源集数和原文。客观剧情会在采用正文后记录；需要判断的内容会单独请你确认。发现不准确的记录时，可纠正或停用。'], visual: 'facts' },
        ],
      },
    ],
  },
  {
    title: '在无限画布中制作',
    items: [
      {
        id: 'canvas-basics',
        title: '认识节点与连线',
        summary: '节点保存内容，连线说明谁参考了谁。',
        eyebrow: '第一次进入无限画布',
        intro: '画布看起来像一桌铺开的素材。别急着整理成艺术展，先完成一次“文字 → 图片 → 视频”，你就懂了。',
        sections: [
          { id: 'what-is-node', title: '什么是节点', paragraphs: ['文字、图片、视频、音频和 3D 导演台都是节点。每个节点保存自己的内容与配置。'], bullets: ['文字节点：提示词、剧本或说明', '图片节点：角色、场景、道具和首帧', '视频节点：生成片段或外部参考视频', '音频节点：台词、声音样本或视频提取音轨', '3D 导演台：摆人物、机位和构图'], visual: 'nodes' },
          { id: 'what-is-edge', title: '什么是连线', paragraphs: ['连线代表引用关系。文字连到图片通常提供提示词，图片连到视频通常提供首帧或角色参考，音频连到视频可以提供声音参考。'], warning: '线连上不等于模型一定支持。节点配置会根据当前模型显示可用输入。', visual: 'canvas' },
          { id: 'first-canvas', title: '第一次建议这样做', steps: ['新建一个文本节点，粘贴一小段场景描述', '从文本节点派生图片节点', '选择模型、比例与参考图后生成', '满意后从图片继续派生视频', '沿连线检查每个结果使用了什么'], tip: '先做一个镜头。第一天就铺满 200 个节点，第二天通常先找昨天的节点。', visual: 'pipeline' },
        ],
      },
      {
        id: 'character-images',
        title: '生成人物图片',
        summary: '先建立稳定的人物参考，再谈姿势、服装和镜头。',
        eyebrow: '让主角每个镜头都像同一个人',
        intro: '人物参考图像证件照：正面、清楚、少遮挡。别拿十人合照让模型猜谁是女主，它没有八卦群。',
        sections: [
          { id: 'character-brief', title: '先写人物视觉卡', paragraphs: ['把年龄感、脸型、气质、发型、服装、时代和禁忌写清楚。不要只写“绝美”。'], prompt: '25 岁东方女性，清冷克制，鹅蛋脸，黑色长发低髻，浅紫宋制褙子，银质发簪；纯白背景，全身正面，写实电影人物设定照。', visual: 'character' },
          { id: 'character-views', title: '先做标准图，再做戏剧镜头', paragraphs: ['图片节点支持三视图、全身正面和半身特写等人物图类型。先确认标准图，再把它连给后续图片或视频节点。'], steps: ['生成正面或三视图', '挑一张最稳定的设为角色素材', '后续节点连接这张参考图', '只改变动作、场景和镜头，不反复重写长相'], visual: 'character-grid' },
          { id: 'character-safety', title: '真人与授权', paragraphs: ['真人图片可能触发内容审核；使用他人肖像、克隆声线或商业素材前，请确认你拥有相应授权。'], warning: '审核失败时，优先改用 AI 生成角色标准图；不要靠反复上传同一张图和审核系统比耐心。', visual: 'shield' },
        ],
      },
      {
        id: 'scene-images',
        title: '生成场景图片',
        summary: '时间、地点、气氛和镜头四件套，比“电影感”三个字可靠。',
        eyebrow: '给故事一个能站得住的地方',
        intro: '场景不是“漂亮背景”。它要能交代时间、藏住线索、限制人物行动，最好还能让美术少问你三次。',
        sections: [
          { id: 'scene-prompt', title: '场景提示词怎么写', paragraphs: ['按“地点与时代 → 时间天气 → 空间结构 → 关键物件 → 光线色彩 → 镜头”组织。'], prompt: '1990 年代南方县城旧照相馆，雨夜，狭长前厅通向暗房，玻璃柜里放着一台损坏的海鸥相机，钨丝灯暖黄，窗外青蓝雨光，35mm 广角建立镜头，无人物。', visual: 'scene' },
          { id: 'scene-reference', title: '把参考图的用途说清楚', paragraphs: ['一张图可以参考建筑结构，另一张只参考色彩。连接后在提示词里明确各自用途，避免模型把所有内容搅成一锅。'], bullets: ['结构参考：门窗、层高、动线', '风格参考：材质、色调、光线', '连续性参考：同一场景不同机位仍保留固定物件'], visual: 'references' },
        ],
      },
      {
        id: 'video-audio',
        title: '视频、声音与参考',
        summary: '图片管长相，声音管声线，提示词管动作和镜头。',
        eyebrow: '让画面开始动起来',
        intro: '视频不是让图片“随便动动”。动作、机位、节奏和结束状态写得越清楚，结果越少像监控录像。',
        sections: [
          { id: 'video-from-image', title: '从图片生成视频', paragraphs: ['将确认过的首帧或角色参考图连入视频节点，选择模型、时长与比例，再描述动作和摄影机运动。'], prompt: '女孩停在门口，先看向桌上的信，再缓慢抬眼望向镜头外的人；镜头轻微推进，雨光在她脸上移动，最后停在欲言又止的表情。', visual: 'video' },
          { id: 'voice-continuity', title: '保持角色声线', paragraphs: ['可以上传音频样本生成台词，或从满意的视频中提取音轨，连给后续视频节点作为参考音频。'], warning: '参考音频需要同时满足当前视频模型的输入要求；不是所有模型都接受声音。以节点配置提示为准。', visual: 'audio' },
        ],
      },
    ],
  },
  {
    title: '审核、资产与交付',
    items: [
      {
        id: 'script-review',
        title: '审核剧本与查看报告',
        summary: '粘贴、上传或选择平台剧本，得到可执行的质量判断。',
        eyebrow: '先判断值不值得改',
        intro: '审核不是夸夸群。结论不好会直接说不好，因为“哪里都挺好”对下一稿没有任何帮助。',
        sections: [
          { id: 'review-entry', title: '三种审核方式', paragraphs: ['在“写剧本 → 审核剧本”选择平台剧本、粘贴文本或上传文件。'], bullets: ['平台剧本：直接检查正在小影里创作的项目', '粘贴文本：最快，适合正在改的稿', '上传文件：支持 PDF、DOCX、DOC、TXT'], visual: 'report' },
          { id: 'read-report', title: '先看结论，再看细节', paragraphs: ['报告会给出等级、分数、商业结论和具体问题。优先处理影响核心冲突、人物动机与可拍性的高优先级问题。'], tip: '不要为了从 79 分改到 82 分把人物灵魂磨没。分数是仪表盘，不是方向盘。', visual: 'score' },
        ],
      },
      {
        id: 'assets-delivery',
        title: '资产复用与交付',
        summary: '角色、场景、声音和成片围绕画布保存、复用与导出。',
        eyebrow: '别让好素材只活在一个节点里',
        intro: '满意的人物图和声线值得留下。下次需要同一个角色时，复用比重新抽卡文明得多。',
        sections: [
          { id: 'save-assets', title: '把画布节点存成资产', paragraphs: ['选中人物图片、场景图片或声音节点后，可存入账号级资产库，并在其他画布中继续引用。'], bullets: ['角色：保留人物标准图与身份', '场景：复用空间结构和视觉风格', '声音：保存声线或原始参考音频'], visual: 'assets' },
          { id: 'delivery-output', title: '查看与下载素材', paragraphs: ['进入“资产中心”，按类型查找素材并打开详情。可在详情里预览文件、查看说明，或点击下载图标打开文件。在其他画布需要复用时，从画布的资产库选择对应素材。'], visual: 'assets' },
        ],
      },
    ],
  },
  {
    title: '排查与说明',
    items: [
      {
        id: 'troubleshooting',
        title: '卡住了，先看这里',
        summary: '生成失败、保存中、节点没反应时，先做最短排查。',
        eyebrow: '先别和刷新键决一死战',
        intro: '大多数问题不是你按错了，也不是 AI 在闹情绪。先看状态，再决定重试、换素材还是等一会儿。',
        sections: [
          { id: 'save-problem', title: '一直显示保存中', paragraphs: ['先确认网络正常并等待状态恢复。不要同时打开多个页面反复修改同一项目。'], visual: 'save' },
          { id: 'generation-problem', title: '生成失败或卡住', bullets: ['阅读节点上的具体提示', '检查图片、视频时长和格式', '减少同框人物或简化动作', '使用“停止/重置”后再生成', '不要连续点击同一个生成按钮'], tip: '红色小叹号比“怎么又不行”更有线索，把鼠标放上去看看它想说什么。', visual: 'trouble' },
          { id: 'unknown-feature', title: '帮助页和界面不一致', paragraphs: ['先确认所在页面，以及账号或团队是否拥有对应权限。如仍找不到入口，可在“提交反馈”中写清页面、按钮名称和操作步骤，并附上截图。'], visual: 'trouble' },
        ],
      },
    ],
  },
];

export const HELP_TOPICS = HELP_GROUPS.flatMap(group => group.items.map(item => ({ ...item, group: group.title })));
